import React, { Component } from "react";
import PropTypes from "prop-types";
import { Button } from "semantic-ui-react";
import { orderApi } from "@api/acquisition";
import { documentRequestApi } from "@api/documentRequests";
import { eItemApi } from "@api/eitems";
import { borrowingRequestApi } from "@api/ill";
import { itemApi } from "@api/items";
import { loanApi } from "@api/loans";
import { invenioConfig } from "@config";
import { goTo } from "@history";
import {
  WeedoutRecordModal,
  WeedoutButton,
} from "@components/backoffice/WeedoutRecordModal/WeedoutRecordModal";
import { formatPidTypeToName } from "@components/backoffice/utils";
import { AcquisitionRoutes, BackOfficeRoutes, ILLRoutes } from "@routes/urls";
import { weededDocumentsApi } from "@api/weededDocuments";
import {
  sendSuccessNotification,
  sendErrorNotification,
} from "@components/Notifications";

const weedoutButton = (props) => {
  return (
    <WeedoutButton
      content={<span style={{ fontSize: "13px" }}>Weed-Out Document</span>}
      labelPosition="left"
      icon="minus circle"
      color="orange"
      {...props}
    />
  );
};

export default class DocumentWeedoutModal extends Component {
  async getRelationRefs() {
    const { relations } = this.props;

    if (!relations || typeof relations !== "object") {
      return {
        data: {
          hits: [],
          total: 0,
        },
      };
    }

    const hits = [];
    for (const [relation, records] of Object.entries(relations)) {
      if (Array.isArray(records)) {
        for (const record of records) {
          const type = formatPidTypeToName(record.pid_type);
          hits.push({
            id: `${type} ${record.pid} (${relation})`,
            record: record,
            type: type,
            pid: record.pid,
          });
        }
      }
    }

    return {
      data: {
        hits: hits,
        total: hits.length,
      },
    };
  }

  handleOnLoanRefClick(loanPid) {
    goTo(BackOfficeRoutes.loanDetailsFor(loanPid));
  }

  handleOnItemRefClick(itemPid) {
    goTo(BackOfficeRoutes.itemDetailsFor(itemPid));
  }

  handleOnEItemRefClick(eitemPid) {
    goTo(BackOfficeRoutes.eitemDetailsFor(eitemPid));
  }

  handleOnRequestRefClick(docReqPid) {
    goTo(BackOfficeRoutes.documentRequestDetailsFor(docReqPid));
  }

  handleOnBrwReqRefClick(brwReqPid) {
    goTo(ILLRoutes.borrowingRequestDetailsFor(brwReqPid));
  }

  handleOnOrderRefClick(orderPid) {
    goTo(AcquisitionRoutes.orderDetailsFor(orderPid));
  }

  createRefProps(documentPid) {
    const loanStates = invenioConfig.CIRCULATION.loanRequestStates.concat(
      invenioConfig.CIRCULATION.loanActiveStates
    );

    return [
      {
        refType: "Loan",
        onRefClick: this.handleOnLoanRefClick,
        getRefData: () => {
          return loanApi.list(
            loanApi.query().withDocPid(documentPid).withState(loanStates).qs()
          );
        },
      },
      {
        refType: "Items",
        onRefClick: this.handleOnItemRefClick,
        getRefData: () => {
          return itemApi.list(itemApi.query().withDocPid(documentPid).qs());
        },
      },
      {
        refType: "DocumentRequest",
        onRefClick: this.handleOnRequestRefClick,
        getRefData: () => {
          return documentRequestApi.list(
            documentRequestApi.query().withDocPid(documentPid).qs()
          );
        },
      },
      {
        refType: "E-items",
        onRefClick: this.handleOnEItemRefClick,
        getRefData: () => {
          return eItemApi.list(eItemApi.query().withDocPid(documentPid).qs());
        },
      },
      {
        refType: "Borrowing requests",
        onRefClick: this.handleOnBrwReqRefClick,
        getRefData: () => {
          return borrowingRequestApi.list(
            borrowingRequestApi.query().withDocPid(documentPid).qs()
          );
        },
      },
      {
        refType: "Orders",
        onRefClick: this.handleOnOrderRefClick,
        getRefData: () => {
          return orderApi.list(orderApi.query().withDocPid(documentPid).qs());
        },
      },
    ];
  }

  handleWeedout = async (documentPid) => {
    try {
      const response = await weededDocumentsApi.weedoutDocument(documentPid);

      if (response.status === "success") {
        sendSuccessNotification(
          "Document weeded out",
          `The document "${
            this.props.document.metadata.title || documentPid
          }" has been successfully weeded out and moved to the weeding queue.`
        );

        if (this.props.onWeedoutSuccess) {
          this.props.onWeedoutSuccess(documentPid);
        }

        setTimeout(() => {
          goTo("/backoffice/weeded-documents");
        }, 500);
      }
    } catch (error) {
      if (
        error.response?.status === 409 &&
        error.response?.data?.error_type === "has_references"
      ) {
        throw error;
      } else {
        const errorMessage =
          error.response?.data?.message || "Failed to weed out document";
        sendErrorNotification("Weedout failed", errorMessage);
        throw error;
      }
    }
  };

  render() {
    const { document } = this.props;

    if (document.metadata.weed_out) {
      return (
        <Button
          fluid
          content="Already Weeded Out"
          labelPosition="left"
          icon="checkmark"
          color="grey"
          disabled
        />
      );
    }

    return (
      <WeedoutRecordModal
        weedoutHeader={`Are you sure you want to weed-out document #${document.metadata.pid}?`}
        weedoutText={
          <div>
            <p>
              This document will be removed from the catalog and moved to the
              weeded documents list for review.
            </p>
          </div>
        }
        refProps={this.createRefProps(document.metadata.pid)}
        onWeedout={() => this.handleWeedout(document.metadata.pid)}
        trigger={weedoutButton}
        weedoutButtonText="Weed-out Document"
        weedoutButtonIcon="minus circle"
        weedoutButtonColor="orange"
      />
    );
  }
}

DocumentWeedoutModal.propTypes = {
  document: PropTypes.object.isRequired,
  relations: PropTypes.object.isRequired,
  onWeedoutSuccess: PropTypes.func,
};

DocumentWeedoutModal.defaultProps = {
  onWeedoutSuccess: null,
};
